/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { multiVariantStyles } from './themes/StyledDatePickerButtonStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { forwardRef } from 'preact/compat';
import type { Ref, JSX } from 'preact';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

type IntrinsicProps = Pick<UnpackSignals<JSX.HTMLAttributes<HTMLElement>>, 'onClick' | 'onKeyDown'>;

type AriaProps = {
  'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];
  'aria-disabled'?: UnpackSignals<JSX.AriaAttributes>['aria-disabled'];
};

type Props = IntrinsicProps &
  AriaProps & {
    /**
     * Specifies that the button appears activated when the user activates the StyledDatePickerButton.
     * When true, adds the :active selector to the button so that it can show active styling.
     * Combine this property with the other properties to get the styling you want.
     */
    isActivable?: boolean;
    /**
     * Specifies the styling of the button to look dimmed.
     * Combine this property with the other properties to get the styling you want.
     */
    isDimmed?: boolean;
    /**
     * Specifies the styling of the button to look hidden, that is, visibility: hidden.
     */
    isHidden?: boolean;
    /**
     * Specifies that the button appears hovered when the user hovers over the StyledDatePickerButton.
     * When true, adds the :hover selector to the button so that it can show hover styling.
     * Combine this property with the other properties to get the styling you want.
     */
    isHoverable?: boolean;
    /**
     * Specifies the styling of the button to look like a day that is adjacent to the current month.
     * Combine this property with the other properties to get the styling you want.
     */
    isAdjacentMonth?: boolean;
    /**
     * Specifies the role of the day cell to be 'button' when isReadonly is false, or no role when isReadonly is true.
     * This helps the screenreader.
     */
    isReadonly?: boolean;
    /**
     * Specifies the styling of the button to look like it is restricted.
     * Combine this property with the other properties to get the styling you want.
     */
    isRestricted?: boolean;
    /**
     * Specifies the styling of the button to look like it is selected.
     * Combine this property with the other properties to get the styling you want.
     */
    isSelected?: boolean;
    /**
     * Specifies the styling of the button to look like it is today.
     * Combine this property with the other properties to get the styling you want.
     */
    isToday?: boolean;
    /**
     * Specifies the label of the day button. This will be the day of the month.
     */
    label: string;
    /**
     * Specifies the tabIndex.
     */
    tabIndex?: 0 | -1;
    /**
     * Specifies the visual variant for the StyledDatePickerButton.
     */
    variant?: 'day' | 'month' | 'year';
  };

/**
 * StyledDatePickerButton is a controlled component that displays a stylized span for a button that goes into a DatePicker.
 */
const StyledDatePickerButton = forwardRef(
  (
    {
      isActivable = false,
      isReadonly = false,
      isAdjacentMonth = false,
      isDimmed = false,
      isHidden = false,
      isHoverable = false,
      isRestricted = false,
      isSelected = false,
      isToday = false,
      tabIndex,
      label,
      variant,
      ...props
    }: Props,
    ref: Ref<HTMLSpanElement> = null
  ) => {
    // The multiVariantStyles function is defined in StyledDatePickerButtonStyles.css.ts,
    // using the recipe utility provided by @vanilla-extract/recipes.
    // This function is used to merge the base styles, variants, and compound variants
    // into a single set of styles for components based on the component's properties.
    const variantClasses = multiVariantStyles({
      active: isActivable ? 'isActivable' : 'notActiveable',
      dimmed: isDimmed ? 'isDimmed' : 'notDimmed',
      hidden: isHidden ? 'isHidden' : 'notHidden',
      hover: isHoverable ? 'isHoverable' : 'notHoverable',
      adjacentMonth: isAdjacentMonth ? 'isAdjacentMonth' : 'notAdjacentMonth',
      restricted: isRestricted ? 'isRestricted' : 'notRestricted',
      selected: isSelected ? 'isSelected' : 'notSelected',
      today: isToday ? 'isToday' : 'notToday',
      variant
    });

    const classes = classNames([variantClasses]);
    return (
      <span
        role={!isReadonly ? 'button' : undefined}
        class={classes}
        ref={ref}
        tabIndex={tabIndex}
        {...props}>
        {label}
      </span>
    );
  }
);

export { StyledDatePickerButton };
